import { Loader2 } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { Band as LiqBand } from '@/features/liqMap';
import type { Level as SrLevel } from '@/features/srLevels';
import {
    isStrong,
    levelColor,
    levelLabel,
    nearestBySide,
} from '@/features/srLevels/palette';
import type {
    ChartingLibraryWidgetConstructor,
    EntityId,
    IChartingLibraryWidget,
    ResolutionString,
} from '@/lib/tv/charting_library';
import { createHlDatafeed, type HlDatafeed } from '@/lib/tv/hlDatafeed';

import { clearChartAnchor, setChartAnchor } from '../chartAnchor';
import type { PositionType } from '../types';

/** Price levels of the active trade, marked on the candle chart. */
export interface ChartPositionMarks {
    type: PositionType;
    entryPrice: number;
    leverage: number;
    takeProfitPrice: number | null;
    /** Armed stop-loss level. */
    stopLossPrice: number | null;
    liquidationPrice: number | null;
    /**
     * A RESTING limit order's price. When set, nothing is open: there is no
     * entry, no liquidation and no armed trigger to draw, only the line the
     * market has to reach. The other fields are null in that case.
     */
    limitPrice?: number | null;
}

// Mark colors — chart theme constants (same lime/red as the candle palette).
const MARK_LIME = '#C8F224';
const MARK_RED = '#FF5C6E';
// A resting order is not a position: its line is deliberately NOT lime (lime is
// action and profit here), just a neutral marker of where the order sits.
const MARK_MUTED = '#98A08A';
const EMPTY_BANDS: LiqBand[] = [];
const EMPTY_LEVELS: SrLevel[] = [];

declare global {
    interface Window {
        TradingView?: { widget: ChartingLibraryWidgetConstructor };
    }
}

let tvScriptPromise: Promise<void> | null = null;

/** Load the charting library once per session (iframe-based, not bundleable). */
async function loadTradingView(): Promise<void> {
    if (window.TradingView) return Promise.resolve();
    tvScriptPromise ??= new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = '/charting_library/charting_library.standalone.js';
        script.onload = () => resolve();
        script.onerror = () => {
            tvScriptPromise = null;
            reject(new Error('Failed to load the TradingView library'));
        };
        document.head.appendChild(script);
    });
    return tvScriptPromise;
}

interface TradingViewChartProps {
    /** Hyperliquid coin symbol, e.g. "BTC". */
    coin: string;
    /** Live mark price from the price WebSocket — ticks the building bar. */
    livePrice: number;
    /** Active trade levels (entry / TP / liq) drawn as labeled lines. */
    position?: ChartPositionMarks | null;
    /** Liquidation clusters drawn as translucent price bands. */
    liqBands?: LiqBand[];
    /** Whether the bands are on; drives the toolbar button's pressed state. */
    liqShown?: boolean;
    /** The toolbar button was pressed. Absent = no button in the toolbar. */
    onToggleLiq?: (next: boolean) => void;
    /** Support/resistance levels, drawn as cyan bands. */
    srLevels?: SrLevel[];
    srShown?: boolean;
    onToggleSr?: (next: boolean) => void;
}

/**
 * Full-history candle chart (TradingView charting library) fed by
 * Hyperliquid's public candles API. The "Candles" side of the chart-type
 * toggle; the physics line chart remains the "Live" side.
 */
const TradingViewChart: React.FC<TradingViewChartProps> = ({
    coin,
    livePrice,
    position = null,
    liqBands = EMPTY_BANDS,
    liqShown = true,
    onToggleLiq,
    srLevels = EMPTY_LEVELS,
    srShown = false,
    onToggleSr,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetRef = useRef<IChartingLibraryWidget | null>(null);
    const datafeedRef = useRef<HlDatafeed | null>(null);
    const chartReadyRef = useRef(false);
    const coinRef = useRef(coin);
    const priceRef = useRef(livePrice);
    const positionRef = useRef<ChartPositionMarks | null>(position);
    const linesRef = useRef<EntityId[]>([]);
    const liqBandsRef = useRef<LiqBand[]>(liqBands);
    const bandIdsRef = useRef<EntityId[]>([]);
    const bandSeqRef = useRef(0);
    const liqShownRef = useRef(liqShown);
    const onToggleLiqRef = useRef(onToggleLiq);
    // the element TradingView hands back from createButton; lives inside the
    // widget's iframe, so it is styled by snappy-theme.css, not by Tailwind
    const liqButtonRef = useRef<HTMLElement | null>(null);
    const srLevelsRef = useRef<SrLevel[]>(srLevels);
    const srIdsRef = useRef<EntityId[]>([]);
    const srSeqRef = useRef(0);
    const srShownRef = useRef(srShown);
    const onToggleSrRef = useRef(onToggleSr);
    const srButtonRef = useRef<HTMLElement | null>(null);
    const [status, setStatus] = useState<'loading' | 'ready' | 'error'>(
        'loading',
    );

    // ── Live-price anchor ────────────────────────────────────────────────
    // Publish the live price's container-relative pixel position (just
    // left of the price axis, next to the building candle) into the shared
    // chartAnchor so the PnL pip rides it — same channel the live chart
    // uses. Polls at 10Hz (the candle pane rescales slowly), and the one
    // getBoundingClientRect here is off the 60fps path.
    useEffect(() => {
        const id = window.setInterval(() => {
            const widget = widgetRef.current;
            const container = containerRef.current;
            if (!widget || !chartReadyRef.current || !container) {
                clearChartAnchor();
                return;
            }
            try {
                const pane = widget.activeChart().getPanes()[0];
                const scale = pane?.getMainSourcePriceScale();
                const range = scale?.getVisiblePriceRange();
                const price = priceRef.current;
                // Canvas box (inside the same-origin widget iframe) mapped
                // to OUR container's coordinate space, so the anchor is
                // container-relative like the live chart's. Falls back to
                // the pane-height API if the DOM probe fails.
                const containerRect = container.getBoundingClientRect();
                let paneTop = 0;
                let paneLeft = 0;
                let paneHeight = pane?.getHeight() ?? 0;
                try {
                    const iframeEl = container.querySelector('iframe');
                    const canvas =
                        iframeEl?.contentDocument?.querySelector('canvas');
                    const rect = canvas?.getBoundingClientRect();
                    const iframeRect = iframeEl?.getBoundingClientRect();
                    if (rect && iframeRect && rect.height > 0) {
                        // The canvas rect is in the IFRAME's viewport
                        // coordinates — translate through the iframe's own
                        // position to get the container-relative offset
                        // (subtracting containerRect directly mixes the
                        // two spaces and shifts the anchor by the panel's
                        // distance from the page edges).
                        paneTop = iframeRect.top + rect.top - containerRect.top;
                        paneLeft =
                            iframeRect.left + rect.left - containerRect.left;
                        paneHeight = rect.height;
                    }
                } catch {
                    // cross-origin/teardown — keep the API fallback
                }
                if (
                    range &&
                    paneHeight > 0 &&
                    price > 0 &&
                    range.to > range.from
                ) {
                    const clamped = Math.min(
                        Math.max(price, range.from),
                        range.to,
                    );
                    const y =
                        paneTop +
                        ((range.to - clamped) / (range.to - range.from)) *
                            paneHeight;
                    // X: at the last candle — the latest bar sits
                    // rightOffset() bars (× barSpacing() px) left of the
                    // time scale's right edge. Clamped to the pane, and
                    // falls back to just-left-of-the-axis if the time
                    // scale is unavailable.
                    let x = Math.max(0, containerRect.width - 72);
                    try {
                        const ts = widget.activeChart().getTimeScale();
                        const lastBarX =
                            paneLeft +
                            ts.width() -
                            ts.rightOffset() * ts.barSpacing();
                        x = Math.min(Math.max(0, lastBarX), x);
                    } catch {
                        // mid-teardown — keep the axis-gutter fallback
                    }
                    setChartAnchor(x, y, performance.now());
                } else {
                    clearChartAnchor();
                }
            } catch {
                clearChartAnchor();
            }
        }, 100);
        return () => {
            window.clearInterval(id);
            clearChartAnchor();
        };
    }, []);

    useEffect(() => {
        coinRef.current = coin;
    }, [coin]);

    // ── Trade-level marks (entry / TP / liq) ─────────────────────────────
    // Drawn as full-width horizontal_line drawings (cro.trade-style: dashed
    // level lines, right-aligned labels, price chips on the axis). NOTE: the
    // nicer createPositionLine/createOrderLine primitives are license-gated —
    // "only available on Trading Platform" — in this Advanced Charts build.
    const clearMarkLines = useCallback(() => {
        const widget = widgetRef.current;
        if (!widget || !chartReadyRef.current) {
            // The chart is not up yet, or is mid-teardown. Forgetting the ids
            // here is what stranded lines on the chart forever: nothing else
            // holds them, so a closed position's entry line stayed drawn and the
            // next position added a second one beside it. Keep them and retry.
            return;
        }
        // Only forget an id once its drawing is actually gone. A removeEntity
        // that throws (a stale id after a symbol switch, a chart being replaced)
        // leaves the line on screen, and dropping the id would make it permanent.
        const survivors: EntityId[] = [];
        for (const id of linesRef.current) {
            try {
                widget.activeChart().removeEntity(id);
            } catch {
                survivors.push(id);
            }
        }
        linesRef.current = survivors;
    }, []);

    /** (Re)draw the entry/TP/liq lines to match the current position.
     *  Async because createShape returns a Promise; syncSeqRef guards
     *  against interleaved syncs (only the latest wins). */
    const syncSeqRef = useRef(0);
    const syncMarkLines = useCallback(async () => {
        if (!chartReadyRef.current || !widgetRef.current) return;
        const seq = ++syncSeqRef.current;
        clearMarkLines();
        const p = positionRef.current;
        if (!p) return;
        // Anything still in linesRef could not be removed this pass and will be
        // retried on the next one. We draw anyway: a stale duplicate line is
        // confusing, but silently dropping the liquidation line would be worse.

        const isLong = p.type === 'LONG';
        const dirColor = isLong ? MARK_LIME : MARK_RED;
        try {
            const chart = widgetRef.current.activeChart();

            const drawLevel = async (
                price: number,
                text: string,
                color: string,
                lineStyle: number,
                lineWidth: number,
            ) => {
                const id = await chart.createShape(
                    { time: 0, price },
                    {
                        shape: 'horizontal_line',
                        text,
                        lock: true,
                        disableSelection: true,
                        disableSave: true,
                        disableUndo: true,
                        overrides: {
                            linecolor: color,
                            linestyle: lineStyle,
                            linewidth: lineWidth,
                            showLabel: true,
                            textcolor: color,
                            fontsize: 11,
                            bold: true,
                            horzLabelsAlign: 'right',
                            vertLabelsAlign: 'top',
                            showPrice: true,
                        },
                    },
                );
                if (seq !== syncSeqRef.current) {
                    try {
                        chart.removeEntity(id);
                    } catch {
                        // a newer sync already cleared the chart
                    }
                    return;
                }
                linesRef.current.push(id);
            };

            // A resting limit order has ONE line: the price it waits for.
            if (p.limitPrice !== null && p.limitPrice !== undefined) {
                await drawLevel(
                    p.limitPrice,
                    `Limit ${isLong ? 'LONG' : 'SHORT'}`,
                    MARK_MUTED,
                    2,
                    1,
                );
                return;
            }

            // Entry — direction-colored dashed line.
            await drawLevel(
                p.entryPrice,
                `Entry ${isLong ? 'LONG' : 'SHORT'} ${p.leverage}×`,
                dirColor,
                2,
                2,
            );

            // Take profit — dashed lime: the win line.
            if (p.takeProfitPrice !== null && p.takeProfitPrice > 0) {
                const roi = Math.round(
                    ((p.takeProfitPrice - p.entryPrice) / p.entryPrice) *
                        p.leverage *
                        100 *
                        (isLong ? 1 : -1),
                );
                await drawLevel(
                    p.takeProfitPrice,
                    `TP +${roi}%`,
                    MARK_LIME,
                    2,
                    1,
                );
            }

            // Stop loss — dashed red, thinner than liquidation.
            if (p.stopLossPrice !== null && p.stopLossPrice > 0) {
                const slRoi = Math.round(
                    ((p.stopLossPrice - p.entryPrice) / p.entryPrice) *
                        p.leverage *
                        100 *
                        (isLong ? 1 : -1),
                );
                await drawLevel(
                    p.stopLossPrice,
                    `SL ${slRoi}%`,
                    MARK_RED,
                    2,
                    1,
                );
            }

            // Liquidation — solid red, the loudest line on the chart.
            if (p.liquidationPrice !== null && p.liquidationPrice > 0) {
                await drawLevel(
                    p.liquidationPrice,
                    'LIQUIDATION',
                    MARK_RED,
                    0,
                    2,
                );
            }
        } catch (error) {
            // Chart mid-teardown is expected; anything else should be loud.
            console.error('[TradingViewChart] mark-line sync failed', error);
        }
    }, [clearMarkLines]);

    useEffect(() => {
        positionRef.current = position;
        void syncMarkLines();
    }, [position, syncMarkLines]);

    // ── Liquidation bands ────────────────────────────────────────────────
    // Same bookkeeping as the mark lines (ids kept until removeEntity succeeds,
    // a sequence so only the latest sync's drawings survive). Bands are
    // rectangles spanning a year either side of now at [low, high]; if the
    // build refuses a rectangle the band degrades to a thick line at its price
    // rather than vanishing.
    const clearLiqBands = useCallback(() => {
        const widget = widgetRef.current;
        if (!widget || !chartReadyRef.current) return;
        const survivors: EntityId[] = [];
        for (const id of bandIdsRef.current) {
            try {
                widget.activeChart().removeEntity(id);
            } catch {
                survivors.push(id);
            }
        }
        bandIdsRef.current = survivors;
    }, []);

    const syncLiqBands = useCallback(async () => {
        if (!chartReadyRef.current || !widgetRef.current) return;
        const seq = ++bandSeqRef.current;
        clearLiqBands();
        const bands = liqBandsRef.current;
        if (bands.length === 0) return;
        const chart = widgetRef.current.activeChart();
        const now = Math.floor(Date.now() / 1000);
        const YEAR = 365 * 24 * 3600;
        for (const band of bands) {
            const color = band.side === 'long' ? MARK_LIME : MARK_RED;
            // TradingView's transparency is 0 (opaque) .. 100 (invisible)
            const transparency = Math.round(100 - (8 + 28 * band.intensity));
            let id: EntityId | null = null;
            try {
                id = await chart.createMultipointShape(
                    [
                        { time: now - YEAR, price: band.low },
                        { time: now + YEAR, price: band.high },
                    ],
                    {
                        shape: 'rectangle',
                        lock: true,
                        disableSelection: true,
                        disableSave: true,
                        disableUndo: true,
                        overrides: {
                            backgroundColor: color,
                            color: color,
                            transparency,
                            linewidth: 0,
                            fillBackground: true,
                            extendLeft: true,
                            extendRight: true,
                            showLabel: false,
                        },
                    },
                );
            } catch {
                try {
                    id = await chart.createShape(
                        { time: 0, price: band.price },
                        {
                            shape: 'horizontal_line',
                            lock: true,
                            disableSelection: true,
                            disableSave: true,
                            disableUndo: true,
                            overrides: {
                                linecolor: color,
                                linewidth: 3,
                                linestyle: 0,
                                showLabel: false,
                                showPrice: false,
                            },
                        },
                    );
                } catch (error) {
                    console.error('[TradingViewChart] liq band failed', error);
                }
            }
            if (id === null) continue;
            if (seq !== bandSeqRef.current) {
                try {
                    chart.removeEntity(id);
                } catch {
                    // a newer sync already cleared the chart
                }
                return;
            }
            bandIdsRef.current.push(id);
        }
    }, [clearLiqBands]);

    useEffect(() => {
        liqBandsRef.current = liqBands;
        void syncLiqBands();
    }, [liqBands, syncLiqBands]);

    // ── Support / resistance ─────────────────────────────────────────────
    // Same bookkeeping as the liquidation bands: ids kept until removeEntity
    // succeeds, a sequence so only the latest sync's drawings survive.
    const clearSrShapes = useCallback(() => {
        const widget = widgetRef.current;
        if (!widget || !chartReadyRef.current) return;
        const survivors: EntityId[] = [];
        for (const id of srIdsRef.current) {
            try {
                widget.activeChart().removeEntity(id);
            } catch {
                survivors.push(id);
            }
        }
        srIdsRef.current = survivors;
    }, []);

    const syncSrShapes = useCallback(async () => {
        if (!chartReadyRef.current || !widgetRef.current) return;
        const seq = ++srSeqRef.current;
        clearSrShapes();
        const levels = srLevelsRef.current;
        if (levels.length === 0) return;
        const chart = widgetRef.current.activeChart();
        const now = Math.floor(Date.now() / 1000);
        const YEAR = 365 * 24 * 3600;
        // the nearest level each side gets its price on the axis; the rest stay quiet
        const nearest = nearestBySide(levels);
        const tagged = new Set([nearest.resistance, nearest.support]);
        for (const level of levels) {
            const color = levelColor(level.kind);
            const strong = isStrong(level);
            const ids: EntityId[] = [];
            // a faint wash between the swings that make the level — no border,
            // the level line is the one edge that should read
            if (level.high > level.low) {
                try {
                    ids.push(
                        await chart.createMultipointShape(
                            [
                                { time: now - YEAR, price: level.low },
                                { time: now + YEAR, price: level.high },
                            ],
                            {
                                shape: 'rectangle',
                                lock: true,
                                disableSelection: true,
                                disableSave: true,
                                disableUndo: true,
                                overrides: {
                                    backgroundColor: color,
                                    color: 'rgba(0, 0, 0, 0)',
                                    transparency: strong ? 90 : 94,
                                    linewidth: 1,
                                    fillBackground: true,
                                    extendLeft: true,
                                    extendRight: true,
                                    showLabel: false,
                                },
                            },
                        ),
                    );
                } catch (error) {
                    console.error('[TradingViewChart] S/R zone failed', error);
                }
            }
            // the level itself: a quiet dotted line on both sides — strength is
            // in the label and the legend, not the stroke (user preference)
            try {
                ids.push(
                    await chart.createShape(
                        { time: 0, price: level.price },
                        {
                            shape: 'horizontal_line',
                            text: levelLabel(level),
                            lock: true,
                            disableSelection: true,
                            disableSave: true,
                            disableUndo: true,
                            overrides: {
                                linecolor: color,
                                linewidth: 1,
                                linestyle: 1,
                                showLabel: true,
                                textcolor: color,
                                fontsize: 11,
                                bold: false,
                                horzLabelsAlign: 'left',
                                vertLabelsAlign: 'top',
                                showPrice: tagged.has(level),
                            },
                        },
                    ),
                );
            } catch (error) {
                console.error('[TradingViewChart] S/R level failed', error);
            }
            if (seq !== srSeqRef.current) {
                for (const id of ids) {
                    try {
                        chart.removeEntity(id);
                    } catch {
                        // a newer sync already cleared the chart
                    }
                }
                return;
            }
            srIdsRef.current.push(...ids);
        }
    }, [clearSrShapes]);

    useEffect(() => {
        srLevelsRef.current = srLevels;
        void syncSrShapes();
    }, [srLevels, syncSrShapes]);

    const paintSrButton = useCallback(() => {
        const button = srButtonRef.current;
        if (!button) return;
        const on = srShownRef.current;
        button.dataset.on = on ? '1' : '0';
        button.setAttribute('aria-pressed', on ? 'true' : 'false');
        button.setAttribute(
            'title',
            on ? 'Hide support and resistance' : 'Show support and resistance',
        );
    }, []);
    useEffect(() => {
        srShownRef.current = srShown;
        onToggleSrRef.current = onToggleSr;
        paintSrButton();
    }, [srShown, onToggleSr, paintSrButton]);

    // The "Liq zones" toggle sits in TradingView's OWN toolbar, next to
    // Indicators — where a trader looks for chart controls — rather than in a
    // React overlay floating over the pane. createButton hands back a bare
    // element inside the iframe; its look comes from snappy-theme.css.
    const paintLiqButton = useCallback(() => {
        const button = liqButtonRef.current;
        if (!button) return;
        const on = liqShownRef.current;
        button.dataset.on = on ? '1' : '0';
        button.setAttribute('aria-pressed', on ? 'true' : 'false');
        button.setAttribute(
            'title',
            on ? 'Hide liquidation zones' : 'Show liquidation zones',
        );
    }, []);
    useEffect(() => {
        liqShownRef.current = liqShown;
        onToggleLiqRef.current = onToggleLiq;
        paintLiqButton();
    }, [liqShown, onToggleLiq, paintLiqButton]);

    // Live WS ticks keep the building bar moving between polls.
    useEffect(() => {
        priceRef.current = livePrice;
        datafeedRef.current?.tickPrice(livePrice);
    }, [livePrice]);

    useEffect(() => {
        let disposed = false;

        loadTradingView()
            .then(() => {
                if (disposed || !containerRef.current || !window.TradingView) {
                    return;
                }

                const datafeed = createHlDatafeed({
                    getCoin: () => coinRef.current,
                    getReferencePrice: () => priceRef.current,
                });
                datafeedRef.current = datafeed;

                const widget = new window.TradingView.widget({
                    container: containerRef.current,
                    datafeed,
                    symbol: coinRef.current,
                    // 15m by default: a 1m chart on a market like BTC shows
                    // ~an hour and a half of noise; 15m shows the day's shape.
                    interval: '15' as ResolutionString,
                    library_path: '/charting_library/',
                    // versioned by the build: the file is fetched inside the
                    // iframe under an unchanging name, so without this a deploy
                    // can keep serving the previous theme from cache
                    custom_css_url: `snappy-theme.css?v=${__APP_COMMIT_HASH__}`,
                    locale: 'en',
                    autosize: true,
                    timezone: 'Etc/UTC',
                    theme: 'dark',
                    toolbar_bg: '#131510',
                    disabled_features: [
                        'header_symbol_search',
                        'header_compare',
                        'symbol_search_hot_key',
                        'display_market_status',
                        'popup_hints',
                        'timeframes_toolbar',
                        'volume_force_overlay',
                        'create_volume_indicator_by_default',
                        'left_toolbar',
                        'control_bar',
                        'legend_widget',
                        'header_fullscreen_button',
                    ],
                    enabled_features: ['hide_left_toolbar_by_default'],
                    overrides: {
                        'paneProperties.background': '#131510',
                        'paneProperties.backgroundType': 'solid',
                        'paneProperties.vertGridProperties.color':
                            'rgba(200, 242, 36, 0.04)',
                        'paneProperties.horzGridProperties.color':
                            'rgba(200, 242, 36, 0.04)',
                        'scalesProperties.backgroundColor': '#131510',
                        'scalesProperties.textColor': '#838874',
                        'scalesProperties.lineColor':
                            'rgba(200, 242, 36, 0.06)',
                        'mainSeriesProperties.candleStyle.upColor': '#C8F224',
                        'mainSeriesProperties.candleStyle.downColor': '#FF5C6E',
                        'mainSeriesProperties.candleStyle.borderUpColor':
                            '#C8F224',
                        'mainSeriesProperties.candleStyle.borderDownColor':
                            '#FF5C6E',
                        'mainSeriesProperties.candleStyle.wickUpColor':
                            'rgba(200, 242, 36, 0.5)',
                        'mainSeriesProperties.candleStyle.wickDownColor':
                            'rgba(255, 92, 110, 0.75)',
                    },
                    studies_overrides: {
                        'volume.volume.color.0': 'rgba(255, 92, 110, 0.15)',
                        'volume.volume.color.1': 'rgba(200, 242, 36, 0.15)',
                    },
                    loading_screen: {
                        backgroundColor: '#131510',
                        foregroundColor: 'rgba(200, 242, 36, 0.3)',
                    },
                });

                widgetRef.current = widget;

                // toolbar button, once the header exists. Skipped entirely when
                // no handler is given, so an embed without the feature gets no
                // dead control.
                if (onToggleLiqRef.current) {
                    void widget.headerReady().then(() => {
                        if (disposed) return;
                        const button = widget.createButton({
                            align: 'left',
                            useTradingViewStyle: false,
                        });
                        // ADD to the placeholder's classes, never replace them:
                        // those classes are what make it look like the
                        // library's own "Indicators" button — same font, same
                        // grey, same hover. apply-common-tooltip is how the
                        // library renders the title.
                        button.classList.add(
                            'snappy-liq-toggle',
                            'apply-common-tooltip',
                        );
                        button.setAttribute('role', 'button');
                        button.setAttribute('tabindex', '0');
                        button.textContent = 'Liq zones';
                        const toggle = () =>
                            onToggleLiqRef.current?.(!liqShownRef.current);
                        button.addEventListener('click', toggle);
                        button.addEventListener('keydown', (event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                toggle();
                            }
                        });
                        liqButtonRef.current = button;
                        paintLiqButton();
                    });
                }
                if (onToggleSrRef.current) {
                    void widget.headerReady().then(() => {
                        if (disposed) return;
                        const button = widget.createButton({
                            align: 'left',
                            useTradingViewStyle: false,
                        });
                        // add to the placeholder's classes, never replace them
                        button.classList.add(
                            'snappy-liq-toggle',
                            'apply-common-tooltip',
                        );
                        button.setAttribute('role', 'button');
                        button.setAttribute('tabindex', '0');
                        button.textContent = 'S/R';
                        const toggle = () =>
                            onToggleSrRef.current?.(!srShownRef.current);
                        button.addEventListener('click', toggle);
                        button.addEventListener('keydown', (event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                toggle();
                            }
                        });
                        srButtonRef.current = button;
                        paintSrButton();
                    });
                }
                widget.onChartReady(() => {
                    chartReadyRef.current = true;
                    setStatus('ready');
                    // Draw everything the parent already holds: the open
                    // trade's levels AND whichever overlays are switched on.
                    // Their prop effects ran before the widget was ready and
                    // bailed; without this they would wait for the next data
                    // tick (up to a minute for liq zones, longer for S/R),
                    // which read as "the overlay vanished when I switched to
                    // candles".
                    void syncMarkLines();
                    void syncLiqBands();
                    void syncSrShapes();
                });
            })
            .catch(() => {
                if (!disposed) setStatus('error');
            });

        return () => {
            disposed = true;
            datafeedRef.current?.destroy();
            datafeedRef.current = null;
            // widget.remove() destroys the mark lines with the chart.
            linesRef.current = [];
            bandIdsRef.current = [];
            srIdsRef.current = [];
            liqButtonRef.current = null;
            srButtonRef.current = null;
            if (widgetRef.current) {
                widgetRef.current.remove();
                widgetRef.current = null;
            }
            chartReadyRef.current = false;
        };
        // Mount once (the sync* and paint* callbacks are stable);
        // coin changes switch the symbol below.
    }, [
        syncMarkLines,
        syncLiqBands,
        syncSrShapes,
        paintLiqButton,
        paintSrButton,
    ]);

    // Switch symbol when the selected market changes.
    useEffect(() => {
        if (widgetRef.current && chartReadyRef.current) {
            widgetRef.current.activeChart().setSymbol(coin, () => {
                void syncMarkLines();
                void syncLiqBands();
                void syncSrShapes();
            });
        }
    }, [coin, syncMarkLines, syncLiqBands, syncSrShapes]);

    return (
        <div className="relative h-full w-full bg-card">
            <div
                ref={containerRef}
                role="img"
                aria-label={`Candlestick chart for ${coin}`}
                className="h-full w-full"
            />
            {status === 'loading' && (
                <div
                    role="status"
                    className="absolute inset-0 flex items-center justify-center"
                >
                    <Loader2
                        size={20}
                        className="animate-spin text-muted-foreground"
                    />
                    <span className="sr-only">Loading chart</span>
                </div>
            )}
            {status === 'error' && (
                <div
                    role="alert"
                    className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground"
                >
                    Chart failed to load, switch back to Live.
                </div>
            )}
        </div>
    );
};

export default TradingViewChart;
